// Homepage pricing section showing annual plans
import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Monitor, Zap, Bot, Settings, ArrowRight } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { Button } from '@/components/ui/button';
import { MicroLabel } from './micro-label';

const homepagePlans = [
  {
    name: "Website Essentials",
    price: "$500/year",
    priceValue: "Website Essentials",
    icon: Monitor,
    features: [
      "Hosting + SSL + uptime monitoring",
      "Up to 6 pages + responsive design",
      "GA4 + on-page SEO basics",
      "1h content updates/month"
    ]
  },
  {
    name: "Growth Website + SEO",
    price: "$800/year", 
    priceValue: "Growth Website + SEO",
    icon: Zap,
    features: [
      "Everything in Essentials",
      "Monthly SEO optimization",
      "1 landing page/quarter",
      "2h updates/month"
    ]
  },
  {
    name: "AI Chatbot",
    price: "$500/year",
    priceValue: "AI Chatbot",
    icon: Bot,
    features: [
      "Smart chatbot for FAQs",
      "Lead capture + routing",
      "Trained on your content",
      "Conversation transcripts"
    ]
  },
  {
    name: "Website + SEO + Chatbot",
    price: "$1,000/year",
    priceValue: "Website + SEO + Chatbot",
    icon: Settings,
    features: [
      "Complete bundle solution",
      "Website + monthly SEO",
      "AI chatbot included",
      "Best value package"
    ]
  }
];

export const PricingSection = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="section">
      <div className="container">
        <div className="section-header">
          <div className="micro-label mb-4">ANNUAL PLANS</div>
          <h2 className="section-title">Transparent pricing with no hidden costs</h2>
          <p className="section-subtitle">
            All plans include ongoing support and post-launch partnership.
          </p>
        </div>

        <div ref={elementRef} className="pricing-grid">
          {homepagePlans.map((plan, index) => {
            const IconComponent = plan.icon;
            
            return (
              <div 
                key={index}
                className="pricing-card"
                style={getRevealStyle(index + 1)}
              >
                <div className="p-6 md:p-7 h-full flex flex-col">
                  {/* Header */}
                  <div className="flex flex-col items-center text-center mb-4 md:mb-6">
                    <div className="w-5 h-5 mb-3 text-primary">
                      <IconComponent 
                        className="w-full h-full" 
                        strokeWidth={1.5} 
                      />
                    </div>
                    
                    <h3 className="font-semibold text-foreground text-base md:text-lg mb-2 leading-tight">
                      {plan.name}
                    </h3>
                    
                    <div className="price">
                      {plan.price}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-4 md:mb-6 flex-1">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-xs md:text-sm text-muted-foreground leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="space-y-2">
                    <Link to={`/contact?plan=${encodeURIComponent(plan.priceValue)}`}>
                      <button className="pricing-cta">
                        Get Started
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Section divider */}
        <div className="section-divider"></div>
          
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-6">
              Need a custom app or web app? <Link to="/contact?plan=Custom App / Web App / MVP" className="text-primary hover:underline">Request a quote</Link>
            </p>
            <Link to="/pricing">
              <Button variant="ghost" className="group">
                View all pricing details
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
      </div>
    </section>
  );
};